import { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Grid } from '@mui/material';
import { ConstructionOption, ObraProperties, ProcesoConstructivo, UnidadObra } from '../../../../../types/ObraDetails';
import { ValidationError } from '../../../../../types/ValidationError';
import styles from './editBuildingProcess.module.scss';
import { SaveContext } from '../../../../../context/SaveProvider';
import AddItemButton from '../../../components/addItemButton/addItemButton';
import DataProcess from './dataProcess/dataProcess';
import ObrasAPI from '../../../../../services/APIObras';
import { TechnicalAspectsContext } from '../../technicalAspects';
import technicalNoData from '../../../../../assets/technicalNoData.svg';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface EditBuildingProcessProps {
  obra: ObraProperties;
  errors?: ValidationError[];
}

/* eslint-disable @typescript-eslint/no-non-null-assertion */
const EditBuildingProcess: FC<EditBuildingProcessProps> = (props) => {
  const { obra, errors } = props;
  const [t, i18n] = useTranslation();
  const saveContext = useContext(SaveContext);
  const [buildingProcess, setbuildingProcess] = useState(obra.aspectosTecnicos?.procesosConstructivos || []);
  const { buildingProcessUnits, setBuildingProcessUnits } = useContext(TechnicalAspectsContext);
  const [chapters, setChapters] = useState<ConstructionOption[]>([]);
  const api = ObrasAPI.getInstance();
  const [chapterError, setChapterError] = useState(false);
  const [reload, setReload] = useState(0);
  const rowKeys = useRef(new WeakMap<ProcesoConstructivo, number>());
  const nextRowKey = useRef(0);

  useEffect(() => {
    let isMounted = true;

    setChapterError(false);
    api.getAllChapter().then((data) => {
      if (!isMounted) return;

      setChapters(data);
    }).catch(() => { if (isMounted) setChapterError(true); });

    return () => {
      isMounted = false;
    };
  }, [api, reload, i18n.language]);

  // adds a new worktype registry with empty data
  const handleAddItem = () => {
    const newProcess: ProcesoConstructivo = { nombre: '', codigo: '', unidades: [] };
    // adds the units related to the new building process
    const newUnits: UnidadObra[] = [];
    newProcess.unidades = newUnits;
    setBuildingProcessUnits([...buildingProcessUnits, { index: buildingProcess.length, units: newUnits }]);
    setbuildingProcess([...buildingProcess, newProcess]);
    if (obra.aspectosTecnicos?.procesosConstructivos) {
      obra.aspectosTecnicos.procesosConstructivos.push(newProcess);
    } else {
      obra.aspectosTecnicos.procesosConstructivos = [newProcess];
    }
    saveContext.handleChangeSave(true);
  };

  const handleDelete = (_data: ProcesoConstructivo, index: number) => {
    obra.aspectosTecnicos.procesosConstructivos.splice(index, 1);
    setbuildingProcess([...obra.aspectosTecnicos.procesosConstructivos]);
    // Refreshes the units to exclude those related to the deleted building process
    setBuildingProcessUnits(obra.aspectosTecnicos?.procesosConstructivos.map((pc, i) => {
      return { index: i, units: pc.unidades?.map(unit => unit) };
    }) || []);
    saveContext.handleChangeSave(true);
  };

  return (
    <>
      <div className={styles.addBtnContainer}>
        <div className={styles.addBtn}>
          <AddItemButton
            addItemText={t('details.technical.buildingProcess.add') as string}
            handleAddItem={handleAddItem}
          />
        </div>
      </div>
      {
        buildingProcess.length === 0 ?
          <div id='buildingProcesses-noDataAdded' className={styles.noDataPic}>
            <img src={technicalNoData} />
            <p>{t('details.technical.noDataAdded')}</p>
          </div>
          : null
      }
      {chapterError && <Alert severity="error" action={<Button onClick={() => setReload((value) => value + 1)}>
        {t('details.technical.buildingProcess.retry')}
      </Button>}>{t('details.technical.buildingProcess.loadError')}</Alert>}
      <Grid container columnSpacing="24px" rowSpacing="0">
        {
          (buildingProcess && buildingProcess.length > 0) ?
            buildingProcess.map((process, index) => {
              const error = errors?.filter(e => parseInt(String(e.index ?? '')) === index);
              if (!rowKeys.current.has(process)) rowKeys.current.set(process, nextRowKey.current++);
              return <DataProcess key={rowKeys.current.get(process)} obra={obra} process={process} index={index} handleDelete={handleDelete} error={error} chapters={chapters}/>;
            })
            : null
        }
      </Grid>
    </>
  );
};

export default EditBuildingProcess;
